import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "../configs/axios";
import { useAuth } from "../auth/AuthContext";

/**
 * Every string — Italian included — comes from the backend
 * (GET /languages/admin/:code), which serves the Italian base with the
 * language's uploaded catalog layered on top, so a missing key arrives in
 * Italian rather than blank. Nothing is bundled: a language added later needs
 * no rebuild. The last catalog per language is cached in localStorage so
 * repeat loads render instantly and survive the API being briefly down.
 */
const APP = "admin";
const CODE_KEY = "i18nLanguage";
/** The base language: shown until the user picks one, and the fallback. */
const DEFAULT_CODE = "it";
const pickAvailable = (c: string, list: Record<string, string>) =>
  list[c] ? c : DEFAULT_CODE;
const CACHE_KEY = "i18nCatalog";

type Catalog = Record<string, any>;
type Vars = Record<string, string | number>;

const lookup = (catalog: Catalog, key: string): unknown =>
  key
    .split(".")
    .reduce<any>((node, part) => (node == null ? undefined : node[part]), catalog);

interface I18nValue {
  code: string;
  languages: Record<string, string>;
  setLanguage: (code: string) => void;
  /** Refetch the language list and the active catalog (after admin edits). */
  reload: () => void;
  /** t("deals.title") / t("common.pageOf", { current: 1, total: 4 }) */
  t: (key: string, vars?: Vars) => string;
}

const I18nContext = createContext<I18nValue>({
  code: "it",
  languages: { it: "Italiano" },
  setLanguage: () => {},
  reload: () => {},
  t: (key) => key,
});

const readCache = (code: string): Catalog | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY}:${code}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [code, setCode] = useState(
    () => localStorage.getItem(CODE_KEY) || DEFAULT_CODE
  );
  const signedIn = !!useAuth().user;

  // Signed in: the language saved on the user wins over this browser's choice.
  useEffect(() => {
    if (!signedIn) return;
    axios
      .get("/user/profile")
      .then((res) => {
        const saved = res.data.data?.language;
        if (saved) setCode(saved);
      })
      .catch(() => {});
  }, [signedIn]);

  const setLanguage = useCallback(
    (next: string) => {
      setCode(next);
      if (signedIn) axios.put("/user/language", { language: next }).catch(() => {});
    },
    [signedIn]
  );
  // null until the first catalog arrives (only on a first-ever visit).
  const [catalog, setCatalog] = useState<Catalog | null>(() => readCache(code));
  const [languages, setLanguages] = useState<Record<string, string>>({
    [DEFAULT_CODE]: "Italiano",
  });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    axios
      .get("/languages")
      .then((res) => {
        const list: Record<string, string> = res.data.data;
        setLanguages(list);
        // The active language was removed (or never set up): fall back.
        setCode((c) => pickAvailable(c, list));
      })
      .catch(() => {});
  }, [version]);

  useEffect(() => {
    localStorage.setItem(CODE_KEY, code);
    document.documentElement.lang = code;

    const cached = readCache(code);
    if (cached) setCatalog(cached);

    let cancelled = false;
    axios
      .get(`/languages/${APP}/${code}`)
      .then((res) => {
        if (cancelled) return;
        setCatalog(res.data.data);
        try {
          localStorage.setItem(
            `${CACHE_KEY}:${code}`,
            JSON.stringify(res.data.data)
          );
        } catch {}
      })
      .catch(() => {
        // Keep whatever we have (cache or previous language) rather than blanking the UI.
      });
    return () => {
      cancelled = true;
    };
  }, [code, version]);

  const t = useCallback(
    (key: string, vars?: Vars) => {
      // The backend already fills gaps with Italian; the key is the last resort.
      const value = catalog ? lookup(catalog, key) : undefined;
      if (typeof value !== "string") return key;
      return vars
        ? value.replace(/\{(\w+)\}/g, (match, name) =>
            vars[name] === undefined ? match : String(vars[name])
          )
        : value;
    },
    [catalog]
  );

  const value = useMemo(
    () => ({ code, languages, setLanguage, reload, t }),
    [code, languages, setLanguage, reload, t]
  );

  if (!catalog) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
export const useT = () => useI18n().t;
